import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowLeft, Globe, Plus, Search, Pencil, Trash2, Upload, Loader2 } from "lucide-react";
import AdminPageBackground from "@/components/admin/AdminPageBackground";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";

interface Partner {
  id: string;
  name_ar: string;
  name_en: string;
  image_url: string | null;
  display_order: number;
}

const emptyForm = { name_ar: "", name_en: "", image_url: "", display_order: 0 };

const Partners = () => {
  const { i18n } = useTranslation();
  const isRTL = i18n.language === "ar";

  const [partners, setPartners] = useState<Partner[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");

  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editing, setEditing] = useState<Partner | null>(null);
  const [form, setForm] = useState(emptyForm);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);

  const [deleteTarget, setDeleteTarget] = useState<Partner | null>(null);

  const fetchPartners = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from("partners")
      .select("*")
      .order("display_order", { ascending: true });
    if (error) {
      toast.error(isRTL ? "خطأ في جلب الشركاء" : "Error fetching partners");
    } else {
      setPartners(data || []);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchPartners();
  }, []);

  const filtered = partners.filter((p) =>
    (isRTL ? p.name_ar : p.name_en).toLowerCase().includes(searchQuery.toLowerCase())
  );

  const openAdd = () => {
    setEditing(null);
    setForm({ ...emptyForm, display_order: partners.length + 1 });
    setIsFormOpen(true);
  };

  const openEdit = (partner: Partner) => {
    setEditing(partner);
    setForm({
      name_ar: partner.name_ar,
      name_en: partner.name_en,
      image_url: partner.image_url || "",
      display_order: partner.display_order,
    });
    setIsFormOpen(true);
  };

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const ext = file.name.split(".").pop();
    const fileName = `${crypto.randomUUID()}.${ext}`;
    const { error } = await supabase.storage.from("partners").upload(fileName, file);
    if (error) {
      toast.error(isRTL ? "فشل رفع الصورة" : "Failed to upload image");
    } else {
      const { data } = supabase.storage.from("partners").getPublicUrl(fileName);
      setForm((f) => ({ ...f, image_url: data.publicUrl }));
      toast.success(isRTL ? "تم رفع الصورة" : "Image uploaded");
    }
    setUploading(false);
  };

  const handleSave = async () => {
    if (!form.name_ar || !form.name_en) {
      toast.error(isRTL ? "يرجى تعبئة الأسماء" : "Please fill in names");
      return;
    }
    setSaving(true);
    const payload = {
      name_ar: form.name_ar,
      name_en: form.name_en,
      image_url: form.image_url || null,
      display_order: Number(form.display_order) || 0,
    };
    const { error } = editing
      ? await supabase.from("partners").update(payload).eq("id", editing.id)
      : await supabase.from("partners").insert(payload);
    if (error) {
      toast.error(isRTL ? "فشل الحفظ" : "Save failed");
    } else {
      toast.success(
        editing
          ? isRTL ? "تم تحديث الشريك" : "Partner updated"
          : isRTL ? "تمت إضافة الشريك" : "Partner added"
      );
      setIsFormOpen(false);
      fetchPartners();
    }
    setSaving(false);
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    const { error } = await supabase.from("partners").delete().eq("id", deleteTarget.id);
    if (error) {
      toast.error(isRTL ? "فشل الحذف" : "Delete failed");
    } else {
      toast.success(isRTL ? "تم حذف الشريك" : "Partner deleted");
      fetchPartners();
    }
    setDeleteTarget(null);
  };

  return (
    <AdminPageBackground className={isRTL ? "rtl" : "ltr"}>
      <nav className="border-b border-white/10 bg-white/5 backdrop-blur-sm">
        <div className="max-w-[2000px] mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <Link to="/admin" className="flex items-center gap-2 text-white/70 hover:text-white transition-colors">
              <ArrowLeft className="w-5 h-5" />
              {isRTL ? "العودة للوحة التحكم" : "Back to Dashboard"}
            </Link>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => i18n.changeLanguage(i18n.language === "en" ? "ar" : "en")}
              className="rounded-full text-white/70 hover:text-white hover:bg-white/10"
              aria-label={isRTL ? "تغيير اللغة" : "Change language"}
            >
              <Globe className="w-5 h-5" />
            </Button>
          </div>
        </div>
      </nav>

      <main className="max-w-[2000px] mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center justify-between mb-8">
          <h1 className="text-3xl font-bold text-white">
            {isRTL ? "إدارة الشركاء" : "Partners Management"}
          </h1>
          <Button onClick={openAdd} className="flex items-center gap-2">
            <Plus className="w-4 h-4" />
            {isRTL ? "إضافة شريك" : "Add Partner"}
          </Button>
        </div>

        <div className="mb-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40 w-5 h-5" />
            <Input
              type="search"
              placeholder={isRTL ? "البحث في الشركاء..." : "Search partners..."}
              className="pl-10 pr-4 bg-white/10 border-white/15 text-white placeholder:text-white/40"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-20">
            <Loader2 className="w-8 h-8 text-white animate-spin" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center text-white/60 py-20">
            {isRTL ? "لا يوجد شركاء" : "No partners found"}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {filtered.map((partner) => (
              <div
                key={partner.id}
                className="bg-white/10 backdrop-blur-md rounded-xl border border-white/15 overflow-hidden"
              >
                <div className="p-4">
                  <div className="w-32 h-32 mx-auto mb-4 bg-white rounded-lg flex items-center justify-center p-2">
                    {partner.image_url ? (
                      <img
                        src={partner.image_url}
                        alt={isRTL ? partner.name_ar : partner.name_en}
                        className="w-full h-full object-contain"
                      />
                    ) : (
                      <span className="text-gray-400 text-xs">
                        {isRTL ? "بدون صورة" : "No image"}
                      </span>
                    )}
                  </div>
                  <h3 className="text-lg font-semibold text-center mb-4 text-white">
                    {isRTL ? partner.name_ar : partner.name_en}
                  </h3>
                  <div className="flex items-center justify-between gap-4">
                    <Button
                      variant="outline"
                      className="flex-1 border-white/20 hover:bg-white/10 text-gold"
                      onClick={() => openEdit(partner)}
                    >
                      <Pencil className="w-4 h-4 mr-2" />
                      {isRTL ? "تعديل" : "Edit"}
                    </Button>
                    <Button
                      variant="destructive"
                      className="flex-1"
                      onClick={() => setDeleteTarget(partner)}
                    >
                      <Trash2 className="w-4 h-4 mr-2" />
                      {isRTL ? "حذف" : "Delete"}
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>

      {/* Add/Edit Dialog */}
      <Dialog open={isFormOpen} onOpenChange={setIsFormOpen}>
        <DialogContent className={isRTL ? "rtl" : "ltr"}>
          <DialogHeader>
            <DialogTitle>
              {editing
                ? isRTL ? "تعديل الشريك" : "Edit Partner"
                : isRTL ? "إضافة شريك" : "Add Partner"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>{isRTL ? "الاسم بالعربية" : "Name (Arabic)"}</Label>
              <Input
                value={form.name_ar}
                onChange={(e) => setForm({ ...form, name_ar: e.target.value })}
              />
            </div>
            <div>
              <Label>{isRTL ? "الاسم بالإنجليزية" : "Name (English)"}</Label>
              <Input
                value={form.name_en}
                onChange={(e) => setForm({ ...form, name_en: e.target.value })}
              />
            </div>
            <div>
              <Label>{isRTL ? "ترتيب العرض" : "Display Order"}</Label>
              <Input
                type="number"
                value={form.display_order}
                onChange={(e) => setForm({ ...form, display_order: Number(e.target.value) })}
              />
            </div>
            <div>
              <Label>{isRTL ? "صورة الشعار" : "Logo Image"}</Label>
              {form.image_url && (
                <div className="my-2 w-24 h-24 bg-white rounded-lg p-2 flex items-center justify-center">
                  <img src={form.image_url} alt="preview" className="max-w-full max-h-full object-contain" />
                </div>
              )}
              <div className="flex items-center gap-2">
                <Input
                  type="file"
                  accept="image/*"
                  onChange={handleImageUpload}
                  disabled={uploading}
                />
                {uploading && <Loader2 className="w-4 h-4 animate-spin" />}
              </div>
              <Input
                className="mt-2"
                placeholder={isRTL ? "أو الصق رابط الصورة" : "Or paste image URL"}
                value={form.image_url}
                onChange={(e) => setForm({ ...form, image_url: e.target.value })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsFormOpen(false)}>
              {isRTL ? "إلغاء" : "Cancel"}
            </Button>
            <Button onClick={handleSave} disabled={saving}>
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {isRTL ? "حفظ" : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirmation */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent className={isRTL ? "rtl" : "ltr"}>
          <AlertDialogHeader>
            <AlertDialogTitle>{isRTL ? "تأكيد الحذف" : "Confirm Delete"}</AlertDialogTitle>
            <AlertDialogDescription>
              {isRTL
                ? `هل أنت متأكد من حذف "${deleteTarget?.name_ar}"؟`
                : `Are you sure you want to delete "${deleteTarget?.name_en}"?`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{isRTL ? "إلغاء" : "Cancel"}</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground">
              {isRTL ? "حذف" : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AdminPageBackground>
  );
};

export default Partners;
